import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { DocumentationCategory } from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import ChipCell from '@lib/components/Table/commonComponents/ChipCell/ChipCell';
import { CellProps, Column, TableAction } from '@lib/components/Table/types';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import CollapseCell from '@lib/components/Table/commonComponents/CollapseCell/CollapseCell';
import LinkedDocumentsCell from './LinkedDocumentsCell';

type ColumnsArgs = {
  onEditHandler: (v: DocumentationCategory) => void;
  t: TFunction<'translation', undefined>;
  isViewOnly?: boolean;
};

export const getColumns = ({
  onEditHandler,
  t,
  isViewOnly,
}: ColumnsArgs): Column<DocumentationCategory>[] => [
  {
    id: 'name',
    Header: t('name'),
    accessor: 'name',
    Cell: ({ item }: CellProps<DocumentationCategory>) => {
      const label = _get(item, 'name');
      const rawColor = _get(item, 'color');
      if (!label || !rawColor) return '-';
      const type = colorToChipType(rawColor);
      return (
        <ChipCell
          label={label}
          type={type}
          leftIcon="circle"
          onClick={isViewOnly ? undefined : () => onEditHandler(item)}
        />
      );
    },
    maxWidth: 300,
    minWidth: 250,
    sortable: true,
  },
  {
    id: 'description',
    Header: t('description'),
    accessor: 'description',
    Cell: (props: CellProps<DocumentationCategory>) => (
      <CollapseCell {...props} minHeight={20} />
    ),
    maxWidth: 300,
    minWidth: 250,
  },
  {
    id: 'documentationsCount',
    Header: t('documents-total'),
    accessorFn: ({ item }: CellProps<DocumentationCategory>) =>
      item.documentationsCount ?? '0',
    Cell: (props: CellProps<DocumentationCategory>) => <TextCell {...props} />,
    maxWidth: 170,
    minWidth: 170,
    width: 170,
    sortable: true,
  },
  {
    id: 'linkedDocuments',
    Header: t('linked-documents'),
    Cell: ({ item }: CellProps<DocumentationCategory>) => (
      <LinkedDocumentsCell item={item} />
    ),
    maxWidth: 250,
    minWidth: 200,
  },
];

type ActionsArgs = {
  onEditHandler: (v: DocumentationCategory) => void;
  onDelete: (v: DocumentationCategory) => void;
  t: TFunction<'translation', undefined>;
  isViewOnly?: boolean;
};

export function getActions({
  onEditHandler,
  onDelete,
  isViewOnly,
  t,
}: ActionsArgs): TableAction<DocumentationCategory>[] {
  if (isViewOnly) return [];
  return [
    {
      id: 'edit-action',
      icon: 'edit',
      onClick: (item: DocumentationCategory) => onEditHandler(item),
      type: IconButtonTypes.PrimaryOutlined,
    },
    {
      id: 'delete-action',
      icon: 'delete',
      onClick: (item: DocumentationCategory) => onDelete(item),
      type: IconButtonTypes.PrimaryOutlined,
      predictorFnDisable: (item: DocumentationCategory) =>
        !item.canDestroy.value,
      predictorFnDisableTooltipProps: () => ({
        body: t('cannot-delete-documentation-category'),
        placement: 'left',
        maxWidth: 200,
      }),
    },
  ];
}
